import { sanitizeAnalyticsUrl } from "./analytics-url";
// LINE ボタンを押した1回を、Meta の Lead と PostHog の line_click で同じ eventID に結ぶ。
// 押した瞬間 (click の capture) に useLpAnalytics が eventID を作り、Lead を送る側がそれを受け取る。
// 同じ押下を2つの経路 (capture の自動記録と onClick の手書き) が拾っても、数えるのは1回にする

export const POSTHOG_KEY = "phc_SpmsnDYPDZ82BoZYnWVDPwJMEL71BRZLrw6zYxb8MD5";
export const POSTHOG_HOST = "https://us.i.posthog.com";

/** この間隔以内の2回目は同じ押下とみなす (onClick の手書きと capture の自動記録、ダブルクリック) */
export const LINE_CLICK_DEDUPE_MS = 1500;
/** 登録した押下の eventID を Lead が受け取れる期間 */
export const LINE_EVENT_ID_TTL_MS = 15000;

type Pending = { id: string; at: number; taken: boolean };
let pending: Pending | null = null;

function newEventId(): string {
  return typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

/** 押下を1回として登録する。duplicate が true なら、すでに数えた押下の繰り返し */
export function registerLineClick(now: number = Date.now()): { eventId: string; duplicate: boolean } {
  if (pending && now - pending.at < LINE_CLICK_DEDUPE_MS) {
    return { eventId: pending.id, duplicate: true };
  }
  pending = { id: newEventId(), at: now, taken: false };
  return { eventId: pending.id, duplicate: false };
}

/**
 * Lead を送る側が、クリックした瞬間に1回だけ呼ぶ。待ってから送る間に次のクリックが来ても、
 * 各クリックは自分の ID を手元に持ったまま送れる。
 * - 直前の押下の ID があれば、それを1回だけ渡す
 * - その押下の2回目 (連打) なら null。呼んだ側は Lead を送らない (PostHog と同じく1回に畳む)
 * - 押下の登録が先に走らない経路では、新しく作る
 */
export function takeLineClickEventId(now: number = Date.now()): string | null {
  if (pending && now - pending.at < LINE_EVENT_ID_TTL_MS) {
    if (pending.taken) return now - pending.at < LINE_CLICK_DEDUPE_MS ? null : fresh(now);
    pending.taken = true;
    return pending.id;
  }
  return fresh(now);
}

function fresh(now: number): string {
  pending = { id: newEventId(), at: now, taken: true };
  return pending.id;
}

/**
 * LINE ボタンの CTA の位置 (data-cta-id)。cta_click の値は LINE 行きだと常に line なので、
 * どの位置が押されたかは別の印 (line_cta_click、PostHog の line_click の ctaId) に残す。
 * 計測の列に入れるので、使える字と長さを絞る
 */
export function readLineCtaId(anchor: { dataset?: { cta?: string; ctaId?: string } } | null | undefined): string | undefined {
  const dataset = anchor?.dataset;
  if (dataset?.cta !== "true") return undefined;
  const id = dataset.ctaId;
  return id && /^[A-Za-z0-9_-]{1,40}$/.test(id) ? id : undefined;
}

/** テスト用 */
export function resetLineClickState() {
  pending = null;
}

type PosthogLike = { capture?: (event: string, properties?: Record<string, unknown>) => void };

/**
 * PostHog へ1件送る。SDK があればそれで、広告 LP のように SDK を読まないページでは
 * 同じ送り先へ sendBeacon で直接送る (SDK は数百KB で 1.2MB の枠に入らない。ここは数百バイト)
 */
export function capturePosthogEvent(
  event: string,
  properties: Record<string, unknown>,
  distinctId: string | null,
) {
  if (typeof window === "undefined") return;
  const posthog = (window as { posthog?: PosthogLike }).posthog;
  if (typeof posthog?.capture === "function") {
    posthog.capture(event, properties);
    return;
  }
  if (!distinctId) return;
  const body = JSON.stringify({
    api_key: POSTHOG_KEY,
    event,
    distinct_id: distinctId,
    properties: { ...properties, $current_url: sanitizeAnalyticsUrl(window.location.href), capturedVia: "beacon" },
    timestamp: new Date().toISOString(),
  });
  const url = `${POSTHOG_HOST}/capture/`;
  try {
    if (navigator.sendBeacon?.(url, new Blob([body], { type: "text/plain" }))) return;
    void fetch(url, { method: "POST", body, keepalive: true, headers: { "Content-Type": "text/plain" } }).catch(() => {});
  } catch {
    // 計測の失敗で LINE への移動を止めない
  }
}
